import { useForm, type StringIndexedObject } from '@kanda-libs/ks-component-ts';
import { useToast } from '@kanda-libs/ks-design-library';
import {
  actions,
  Company,
  services,
  useSubmit,
  type Document,
  type Service,
} from '@kanda-libs/ks-frontend-services';
import type {
  ContractPdfResponse,
  ContractRequestBody,
  ContractRequestBodyBody,
} from '@kanda-libs/ks-frontend-services/src/service/external';
import { useAppDispatch } from 'components/App';
import { URLS } from 'config';
import { useCurrentCompany, useMe } from 'hooks';
import useApiError from 'hooks/useApiError';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import { cleanObject, scrollToTop } from 'utils';
import { formatTermsBody } from './helpers';

export interface TermsFields {
  signee: string;
  authorised: boolean;
  becomeIar: boolean;
  tac: {
    ip?: string;
    timestamp?: number;
    value: boolean;
  };
  document: Document;
}

export interface TermsAndConditionsFormHook {
  form: UseFormReturn<TermsFields>;
  onSubmit: (values: StringIndexedObject<TermsFields>) => void;
  disabled: boolean;
  body: ContractRequestBodyBody | undefined;
  isSubmitting: boolean;
}

export default function useTermsAndConditionsForm(): TermsAndConditionsFormHook {
  const { me } = useMe();
  const { company } = useCurrentCompany();
  const { showError, showSuccess } = useToast();
  const dispatch = useAppDispatch();
  const { push } = useHistory();
  const onError = useApiError(
    'Error updating company info at this time - please try again later',
  );

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const form = useForm<TermsFields>({
    mode: 'onBlur',
  });

  const [signee, authorised, becomeIar, tacAgree, tacTimestamp, tacIp] =
    form.watch([
      'signee',
      'authorised',
      'becomeIar',
      'tac.value',
      'tac.timestamp',
      'tac.ip',
    ]);

  const [debouncedSignee] = useDebounce(signee, 1000);

  const fingerprint = useMemo(() => {
    const email = me?.email;
    if (!tacAgree) return email ?? '';
    const parsedDate = tacTimestamp
      ? new Date(tacTimestamp).toISOString()
      : undefined;
    return [email, tacIp, parsedDate].filter(Boolean).join(';');
  }, [me, tacAgree, tacIp, tacTimestamp]);

  const { submit: getPdf } = useSubmit(
    services.contractv2.contractpdf as unknown as Service<
      ContractPdfResponse,
      object,
      ContractRequestBody
    >,
  );

  const body = useMemo(
    () => formatTermsBody(company, me, fingerprint, debouncedSignee),
    [company, me, fingerprint, debouncedSignee],
  );

  const name = useMemo(
    () => `Ideal4Finance-T&C-${company?.id ?? ''}.pdf`,
    [company],
  );

  const submit = useCallback(
    (fields: TermsFields) => {
      if (!company) return;
      setIsSubmitting(true);
      const putBody: Company = cleanObject({
        ...company,
        company_info: {
          ...company?.company_info,
          contract_agreement: {
            contract: {
              name: fields.document.name,
              content: fields.document.content,
            },
            agreed_terms_and_conditions: 'yes',
            agreed_to_become_iar_of_kanda: 'yes',
            print_name: fields.signee,
          },
        },
      });
      const id = company?.id;
      if (!putBody || !id) {
        setIsSubmitting(false);
        showError('Error with data in company - please contact Ideal4Finance');
        return;
      }
      dispatch(
        actions.putCompany({
          body: putBody,
          params: { id },
          onError: (error) => {
            setIsSubmitting(false);
            onError(error);
          },
          onSuccess: () => {
            setIsSubmitting(false);
            showSuccess(
              'Details submitted successfully, your account is now in review',
            );
            push(URLS.exampleJob);
            scrollToTop();
          },
        }),
      );
    },
    [showSuccess, dispatch, onError, push, showError, company],
  );

  const fetchAndSubmit = useCallback(
    (fields: TermsFields, requestBody: ContractRequestBodyBody) => {
      setIsSubmitting(true);
      getPdf({ body: requestBody as unknown as ContractRequestBody }).then(
        ({ data, error: err }) => {
          setIsSubmitting(false);
          if (err) {
            showError(`Error encountered: ${err}`);
            return;
          }
          if (!data) {
            showError('Data missing in response - please try again');
            return;
          }
          submit({
            ...fields,
            document: {
              content: data.base64,
              name,
            },
          });
        },
      );
    },
    [name, getPdf, showError, submit],
  );

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<TermsFields>) => {
      if (!body || !company) return;
      const fields = formValues as unknown as TermsFields;
      if (!fields.document) {
        fetchAndSubmit(fields, body);
        return;
      }
      submit(fields);
    },
    [body, fetchAndSubmit, submit, company],
  );

  const disabled = useMemo(
    () => !authorised || !becomeIar || !tacAgree || isSubmitting,
    [authorised, becomeIar, tacAgree, isSubmitting],
  );

  useEffect(() => {
    if (!me) return;
    form.setValue('signee', me.name);
  }, [me, form]);

  return {
    form,
    onSubmit,
    disabled,
    body,
    isSubmitting,
  };
}
